// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { useGasv3Listener } from '@atlassian/post-office-context';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';
import { defineMessages, useIntl } from 'react-intl-next';
import { useFireEngagementPlatformAnalyticsEvents } from '@post-office/analytics';
import { useEffect } from 'react';

import spotlightImage from './assets/spotlight-image.png';
import { createRender } from './types';

/**
 * ⚠️  CRITICAL SAFETY WARNING FOR POST OFFICE MESSAGES ⚠️
 *
 * Messages in Post Office can go live and reach customers very easily, particularly
 * with implicit message creation. Before merging this code, ensure you have:
 *
 * 1. ✅ Set up a Statsig filter to control message visibility
 *    📖 https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#statsig-filter
 *
 * 2. ✅ Set up an environment filter to prevent accidental production deployment
 *    📖 https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#environment-filter
 *
 * 3. ✅ If using implicit messages, configure them properly in implicit-messages
 *    📖 https://developer.atlassian.com/platform/post-office/message-template/implicit-messages/#what-are-implicit-messages
 *
 * 4. ✅ Verify that you're not rendering anything you dont want customers to see.
 *    📖 https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/213556
 *
 * Remember: Test thoroughly in staging environments before enabling in production!
 */

export const formattedMessages = defineMessages({
	spotlightBody: {
		id: 'INAPP-2131.spotlight.body',
		defaultMessage:
			"We've added Confluence so you can organize all of your project documentation. It's free for up to 10 users.",
		description: 'Body for the spotlight',
	},
	spotlightDismiss: {
		id: 'INAPP-2131.spotlight.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button for the spotlight',
	},
});

export const render = createRender(
	({ onDismissed, messageInstanceId, userId, tenantId, locale }) => {
		const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
		const { fireAnalyticsEvent } = useFireEngagementPlatformAnalyticsEvents();
		const showMessage = useGasv3Listener({
			action: 'dwelled',
			actionSubject: 'page',
		});
		const { formatMessage } = useIntl();

		const handleDismissClick = () => {
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'dismiss',
					clickedElement: 'button',
					buttonType: 'primary',
				},
				action: { actionType: 'dismiss' },
				additionalMetadataFromFrontend: {},
			});
			onDismissed(messageInstanceId);
		};
		useEffect(() => {
			if (showMessage) {
				void messageInstanceViewed();
				if (userId && tenantId) {
					fireAnalyticsEvent({
						messageType: 'inapp',
						messageId: 'INAPP-2131',
						userId,
						tenantId,
						locale,
					});
				}
			}
		}, [showMessage, tenantId, userId, locale, fireAnalyticsEvent, messageInstanceViewed]);

		return (
			<ManualMessageViewedEvent>
				{showMessage ? (
					<SpotlightTransition>
						<Spotlight
							image={spotlightImage}
							actions={[
								{
									onClick: handleDismissClick,
									text: formatMessage(formattedMessages.spotlightDismiss),
									appearance: 'primary',
								},
							]}
							target="app-switcher-spotlight"
							key="app-switcher-spotlight"
							targetRadius={3}
							dialogWidth={275}
						>
							{formatMessage(formattedMessages.spotlightBody)}
						</Spotlight>
					</SpotlightTransition>
				) : null}
			</ManualMessageViewedEvent>
		);
	},
);

export default render;
